import { useAuthStore } from '@/stores/authStore'
import {
  LoginFormData,
  RegisterFormData,
  APIKeyLoginFormData,
  ForgotPasswordFormData,
  ResetPasswordFormData,
  ChangePasswordFormData,
  UpdateProfileFormData,
} from '@/types'

/**
 * Custom hook for authentication functionality
 * Provides a clean interface to the auth store with proper error handling
 */
export const useAuth = () => {
  const store = useAuthStore()

  return {
    // State
    user: store.user,
    tokens: store.tokens,
    isLoading: store.isLoading,
    isAuthenticated: store.isAuthenticated,
    authMethod: store.authMethod,

    // Standard Authentication
    login: store.login,
    register: store.register,
    logout: store.logout,

    // API Key Authentication
    loginWithAPIKey: store.loginWithAPIKey,
    logoutAPIKey: store.logoutAPIKey,

    // Email Verification
    verifyEmail: store.verifyEmail,
    resendVerificationEmail: store.resendVerificationEmail,

    // Password Management
    forgotPassword: store.forgotPassword,
    resetPassword: store.resetPassword,
    changePassword: store.changePassword,

    // Profile Management
    updateProfile: store.updateProfile,

    // Admin User Management
    getAdminUsers: store.getAdminUsers,
    searchAdminUsers: store.searchAdminUsers,

    // Utility Methods
    checkAuth: store.checkAuth,
    updateUser: store.updateUser,
    clearAuth: store.clearAuth,
  }
}

/**
 * Hook for login functionality with form handling
 */
export const useLogin = () => {
  const { login, isLoading } = useAuth()

  const handleLogin = async (data: LoginFormData) => {
    try {
      await login(data)
      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  return {
    login: handleLogin,
    isLoading,
  }
}

/**
 * Hook for registration functionality with form handling
 */
export const useRegister = () => {
  const { register, isLoading } = useAuth()

  const handleRegister = async (data: RegisterFormData) => {
    try {
      await register(data)
      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  return {
    register: handleRegister,
    isLoading,
  }
}

/**
 * Hook for API key authentication
 */
export const useAPIKeyAuth = () => {
  const { loginWithAPIKey, logoutAPIKey, isLoading } = useAuth()

  const handleAPIKeyLogin = async (data: APIKeyLoginFormData) => {
    try {
      await loginWithAPIKey(data)
      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  const handleAPIKeyLogout = async () => {
    try {
      await logoutAPIKey()
      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  return {
    loginWithAPIKey: handleAPIKeyLogin,
    logoutAPIKey: handleAPIKeyLogout,
    isLoading,
  }
}

/**
 * Hook for password management
 */
export const usePasswordManagement = () => {
  const { forgotPassword, resetPassword, changePassword, isLoading } = useAuth()

  const handleForgotPassword = async (data: ForgotPasswordFormData) => {
    try {
      await forgotPassword(data)
      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  const handleResetPassword = async (data: ResetPasswordFormData) => {
    try {
      await resetPassword(data)
      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  const handleChangePassword = async (data: ChangePasswordFormData) => {
    try {
      await changePassword(data)
      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  return {
    forgotPassword: handleForgotPassword,
    resetPassword: handleResetPassword,
    changePassword: handleChangePassword,
    isLoading,
  }
}

/**
 * Hook for email verification
 */
export const useEmailVerification = () => {
  const { verifyEmail, resendVerificationEmail, isLoading } = useAuth()

  const handleVerifyEmail = async (token: string) => {
    try {
      await verifyEmail(token)
      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail()
      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  return {
    verifyEmail: handleVerifyEmail,
    resendVerificationEmail: handleResendVerification,
    isLoading,
  }
}

/**
 * Hook for profile management
 */
export const useProfile = () => {
  const { user, updateProfile, isLoading } = useAuth()

  const handleUpdateProfile = async (data: UpdateProfileFormData) => {
    try {
      await updateProfile(data)
      return { success: true }
    } catch (error) {
      return { success: false, error }
    }
  }

  return {
    user,
    updateProfile: handleUpdateProfile,
    isLoading,
  }
}

/**
 * Hook for admin user management
 */
export const useAdminUsers = () => {
  const { getAdminUsers, searchAdminUsers, isLoading } = useAuth()

  const handleGetAdminUsers = async (params?: {
    search?: string
    page?: number
    page_size?: number
  }) => {
    try {
      const result = await getAdminUsers(params)
      return { success: true, data: result }
    } catch (error) {
      return { success: false, error }
    }
  }

  const handleSearchAdminUsers = async (query: string) => {
    try {
      const result = await searchAdminUsers(query)
      return { success: true, data: result }
    } catch (error) {
      return { success: false, error }
    }
  }

  return {
    getAdminUsers: handleGetAdminUsers,
    searchAdminUsers: handleSearchAdminUsers,
    isLoading,
  }
}
